import { useId, useState, useEffect, useMemo } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getFeedbackList, getFeedbackDetails, submitFeedback } from '../api';
import { PlusIcon } from '../components/Icons';
import {
  Button,
  CustomSelect,
  SearchInput,
  SkeletonBox,
  SideDrawer,
} from '@/components';
import { V2Table } from '@/common/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { env } from '@/common/config';
import uploadIcon from '../assets/upload.png';
import {
  DashboardPage,
  DashboardHeader,
  DashboardFilterBar,
} from '../components/PremiumDesign';

export function FeedbackPage() {
  const feedbackFieldId = useId();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    category: 'General Feedback',
    message: '',
    rating: 5,
    attachment: null,
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  let currentCompanyId =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (!currentCompanyId) {
    try {
      const userStr =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        currentCompanyId = user?.company_id || user?.company?.id || user?.id;
      }
    } catch {
      // ignore parsing error
    }
  }

  const {
    data: feedbackData,
    isLoading: isFeedbackInitialLoading,
    isFetching: isFeedbackFetching,
  } = useQuery({
    queryKey: ['feedback-list', currentCompanyId, debouncedSearch, page],
    queryFn: async () => {
      if (!currentCompanyId) return { results: [] };
      const params = debouncedSearch ? { search: debouncedSearch } : {};
      if (page > 1) params.page = page;
      return getFeedbackList(currentCompanyId, params);
    },
    enabled: !!currentCompanyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFeedbackFetching && feedbackData !== undefined,
    300,
    500,
  );
  const isFeedbackLoading = isFeedbackInitialLoading || showDelayedSkeleton;

  const { data: feedbackDetails, isLoading: isDetailsApiLoading } = useQuery({
    queryKey: ['feedback-details', currentCompanyId, selectedFeedbackId],
    queryFn: async () => {
      if (!currentCompanyId || !selectedFeedbackId) return null;
      return getFeedbackDetails({
        companyId: currentCompanyId,
        feedbackId: selectedFeedbackId,
      });
    },
    enabled: !!currentCompanyId && !!selectedFeedbackId,
  });

  const isDetailsLoading = useDelayedSkeleton(isDetailsApiLoading);

  const { mutate: handleSubmitFeedback, isPending: isSubmitting } = useMutation(
    {
      mutationFn: submitFeedback,
      onSuccess: () => {
        toast.success('Feedback submitted successfully!');
        setIsSubmitModalOpen(false);
        setFormData({
          name: '',
          subject: '',
          category: 'General Feedback',
          message: '',
          rating: 5,
          attachment: null,
        });
        queryClient.invalidateQueries({
          queryKey: ['feedback-list', currentCompanyId],
        });
      },
      onError: (err) => {
        const errorData = err.response?.data;
        toast.error(
          errorData?.message ||
            errorData?.detail ||
            'Failed to submit feedback.',
        );
      },
    },
  );

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (
      !formData.subject ||
      !formData.message ||
      !formData.category ||
      !formData.rating
    ) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const payload = new FormData();
    if (formData.name) payload.append('name', formData.name);
    payload.append('subject', formData.subject);
    payload.append('category', formData.category);
    payload.append('message', formData.message);
    payload.append('rating', formData.rating);
    if (formData.attachment instanceof File) {
      payload.append('attachment', formData.attachment);
    }

    handleSubmitFeedback({ companyId: currentCompanyId, payload });
  };

  const feedbackList = feedbackData?.results || [];
  const count = feedbackData?.count || 0;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    const time = d.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${day} ${month} ${year}, ${time}`;
  };

  const columns = useMemo(
    () => [
      {
        id: 'subject',
        label: 'Subject',
        defaultWidth: 280,
        minWidth: 180,
        cell: (feedback) => (
          <span className="truncate block" title={feedback.subject || ''}>
            {feedback.subject || '-'}
          </span>
        ),
      },
      {
        id: 'category',
        label: 'Category',
        defaultWidth: 180,
        minWidth: 130,
        cell: (feedback) => feedback.category || 'General',
      },
      {
        id: 'rating',
        label: 'Rating',
        defaultWidth: 120,
        minWidth: 100,
        cell: (feedback) => (
          <div className="flex items-center gap-1 w-fit px-2.5 py-1 bg-[#EBF4EA] text-primary rounded-md text-sm font-medium">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
            {feedback.rating ? Number(feedback.rating).toFixed(1) : '-'}
          </div>
        ),
      },
      {
        id: 'created',
        label: 'Created Date',
        defaultWidth: 200,
        minWidth: 160,
        cell: (feedback) => formatDate(feedback.created_at),
      },
      {
        id: 'action',
        label: 'Action',
        defaultWidth: 100,
        minWidth: 80,
        align: 'center',
        stopPropagation: true,
        cell: (feedback) => (
          <button
            type="button"
            onClick={() => setSelectedFeedbackId(feedback.id)}
            className="p-1 hover:bg-layer-7-hover rounded transition-all focus:outline-none text-[#4880FF]"
            title="View Details"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <DashboardPage className="h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="shrink-0">
        <DashboardHeader title="Feedback" />
      </div>

      <div className="shrink-0">
        <DashboardFilterBar>
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by subject or category"
            className="w-full sm:w-auto sm:flex-grow sm:max-w-md rounded-[12px] order-2 sm:order-1"
          />
          <Button
            variant="primary"
            onClick={() => setIsSubmitModalOpen(true)}
            className="w-full sm:w-auto h-[40px] px-4 order-1 sm:order-2"
            icon={<PlusIcon />}
          >
            <span>Submit Feedback</span>
          </Button>
        </DashboardFilterBar>
      </div>

      <div className="w-full min-w-0 flex flex-col flex-1 min-h-0">
        <V2Table
          tight
          storageKey="feedback_table_column_widths"
          columns={columns}
          data={feedbackList}
          rowKey="id"
          loading={isFeedbackLoading || !feedbackData}
          empty="No feedback found"
          pagination={{
            currentPage: page,
            totalCount: count,
            itemsPerPage: 10,
            onChange: setPage,
          }}
        />
      </div>

      {/* Details Drawer */}
      {/* Details Drawer */}
      <SideDrawer
        isOpen={!!selectedFeedbackId}
        onClose={() => setSelectedFeedbackId(null)}
        width="md"
        headerClassName="px-6 py-5"
        className="!max-w-[440px]"
        header={
          <div className="flex items-center justify-between w-full">
            <h3 className="text-[20px] font-bold text-text-primary">
              Feedback Details
            </h3>
            <button
              onClick={() => setSelectedFeedbackId(null)}
              className="w-7 h-7 rounded-[8px] bg-primary text-white flex items-center justify-center hover:opacity-95 transition-all focus:outline-none shrink-0"
            >
              <svg
                className="w-4 h-4 stroke-[3]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-6">
          {isDetailsLoading ? (
            <div className="flex flex-col gap-4">
              <SkeletonBox className="h-40 w-full" rounded="rounded-2xl" />
              <SkeletonBox className="h-24 w-full" rounded="rounded-2xl" />
            </div>
          ) : feedbackDetails ? (
            <>
              {/* Details Card */}
              <div className="bg-[#F8F9FA] rounded-[16px] p-5 flex flex-col gap-3.5 border border-border-default">
                <div className="flex justify-between items-center">
                  <span className="text-[13px] text-text-muted">Category</span>
                  <span className="text-[13px] font-medium text-text-primary text-right">
                    {feedbackDetails.category || 'General'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] text-text-muted">Subject</span>
                  <span className="text-[13px] font-medium text-text-primary text-right">
                    {feedbackDetails.subject || '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] text-text-muted">Rating</span>
                  <div className="flex items-center gap-1 bg-[#EBF4EA] text-primary rounded-md px-2 py-0.5 text-[12px] font-medium">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                      />
                    </svg>
                    {feedbackDetails.rating
                      ? Number(feedbackDetails.rating).toFixed(1)
                      : '-'}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[13px] text-text-muted">
                    Submitted Date
                  </span>
                  <span className="text-[13px] font-medium text-text-primary text-right">
                    {formatDate(feedbackDetails.created_at)}
                  </span>
                </div>
              </div>

              {/* Message Section */}
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-text-muted px-1">
                  Message
                </span>
                <div className="bg-white border border-border-default rounded-[16px] p-5 text-[13px] font-medium text-text-primary leading-relaxed whitespace-pre-wrap shadow-sm">
                  {feedbackDetails.message || 'No message provided.'}
                </div>
              </div>

              {/* Attachment Section */}
              {feedbackDetails.attachment && (
                <div className="flex flex-col gap-2">
                  <span className="text-[13px] text-text-muted px-1">
                    Attachment
                  </span>
                  {(() => {
                    const url = feedbackDetails.attachment.startsWith('http')
                      ? feedbackDetails.attachment
                      : `${env.photoBaseUrl || env.apiBaseUrl || 'http://localhost:8000'}${feedbackDetails.attachment.startsWith('/') ? '' : '/'}${feedbackDetails.attachment}`;

                    return url.match(/\.(jpeg|jpg|gif|png|webp|svg|heic)$/i) ? (
                      <img
                        src={url}
                        alt="Attachment"
                        className="w-full h-auto rounded-[16px] object-cover shadow-sm border border-border-default"
                      />
                    ) : (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#F8F9FA] border border-border-default text-primary rounded-[16px] font-medium hover:bg-gray-100 transition-colors w-full h-[120px] shadow-sm"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                        </svg>
                        View Attachment Document
                      </a>
                    );
                  })()}
                </div>
              )}
            </>
          ) : (
            <div className="py-8 text-center text-text-muted">
              Failed to load details.
            </div>
          )}
        </div>
      </SideDrawer>

      {/* Submit Drawer */}
      <SideDrawer
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        width="md"
        headerClassName="px-6 py-5 border-b border-[#EBEBEB]"
        className="!max-w-[480px]"
        labelledBy={`${feedbackFieldId}-dialog-title`}
        header={
          <div className="flex items-center justify-between w-full">
            <h3
              id={`${feedbackFieldId}-dialog-title`}
              className="text-[20px] font-bold text-text-primary"
            >
              Submit Feedback
            </h3>
            <button
              type="button"
              aria-label="Close dialog"
              onClick={() => setIsSubmitModalOpen(false)}
              className="w-7 h-7 rounded-[8px] bg-primary text-white flex items-center justify-center hover:opacity-95 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white shrink-0"
            >
              <svg
                className="w-4 h-4 stroke-[3]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
      >
        <form onSubmit={handleFormSubmit} className="flex flex-col h-full">
          <div className="overflow-y-auto custom-scrollbar flex flex-col flex-1">
            <h4 className="text-primary font-medium text-[15px] mb-4">
              Basic Information
            </h4>

            <div className="bg-[#F5F5F5] rounded-[16px] p-5 flex flex-col gap-5">
              <div className="grid grid-cols-2 gap-4">
                {/* Category */}
                <div>
                  <label
                    id={`${feedbackFieldId}-category-label`}
                    htmlFor={`${feedbackFieldId}-category`}
                    className="block text-[13px] font-medium text-text-secondary mb-1.5"
                  >
                    Category <span className="text-red-500">*</span>
                  </label>
                  <CustomSelect
                    id={`${feedbackFieldId}-category`}
                    aria-labelledby={`${feedbackFieldId}-category-label`}
                    value={formData.category}
                    onChange={(val) =>
                      setFormData({ ...formData, category: val })
                    }
                    options={[
                      { label: 'Suggestion', value: 'Suggestion' },
                      { label: 'Complaint', value: 'Complaint' },
                      { label: 'Bug Report', value: 'Bug Report' },
                      { label: 'Feature Request', value: 'Feature Request' },
                      { label: 'General Feedback', value: 'General Feedback' },
                      { label: 'Other', value: 'Other' },
                    ]}
                    placeholder="Select category"
                    textSizeClass="text-[13px]"
                    buttonClassName="w-full h-[40px] bg-white border border-[#EBEBEB] rounded-lg px-3 text-text-primary text-[13px] focus:outline-none focus:border-primary transition-all flex items-center justify-between text-left shadow-sm"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label
                    htmlFor={`${feedbackFieldId}-subject`}
                    className="block text-[13px] font-medium text-text-secondary mb-1.5"
                  >
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={`${feedbackFieldId}-subject`}
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                    placeholder="Feedback subject"
                    className="w-full h-[40px] bg-white border border-[#EBEBEB] rounded-lg px-3 text-[13px] text-text-primary placeholder-text-muted focus:outline-none focus:border-primary transition-all shadow-sm"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor={`${feedbackFieldId}-message`}
                  className="block text-[13px] font-medium text-text-secondary mb-1.5"
                >
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id={`${feedbackFieldId}-message`}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="w-full p-3 rounded-lg bg-white border border-[#EBEBEB] text-text-primary text-[13px] placeholder-text-muted focus:outline-none focus:border-primary transition-all min-h-[90px] custom-scrollbar resize-none shadow-sm"
                  placeholder="Tell us what you think..."
                />
              </div>

              {/* Rating */}
              <div>
                <label
                  id={`${feedbackFieldId}-rating-label`}
                  htmlFor={`${feedbackFieldId}-rating`}
                  className="block text-[13px] font-medium text-text-primary mb-1.5"
                >
                  Rating <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        aria-label={`Set rating to ${star}`}
                        className="cursor-pointer relative w-10 h-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        onClick={() =>
                          setFormData({ ...formData, rating: star })
                        }
                      >
                        <svg
                          aria-hidden="true"
                          className={`absolute top-0 left-0 w-10 h-10 ${formData.rating >= star ? 'text-primary' : 'text-white'}`}
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        {formData.rating > star - 1 &&
                          formData.rating < star && (
                            <svg
                              aria-hidden="true"
                              className="absolute top-0 left-0 w-10 h-10 text-primary"
                              style={{ clipPath: 'inset(0 50% 0 0)' }}
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                            </svg>
                          )}
                      </button>
                    ))}
                  </div>
                  <input
                    id={`${feedbackFieldId}-rating`}
                    type="number"
                    min="0"
                    max="5"
                    step="0.5"
                    value={formData.rating}
                    onChange={(e) => {
                      setFormData({ ...formData, rating: e.target.value });
                    }}
                    onBlur={(e) => {
                      let val = parseFloat(e.target.value);
                      if (isNaN(val)) val = 0;
                      if (val > 5) val = 5;
                      if (val < 0) val = 0;
                      val = Math.round(val * 2) / 2;
                      setFormData({ ...formData, rating: val });
                    }}
                    className="w-[50px] h-[40px] text-center text-[14px] font-bold bg-white rounded-lg text-text-primary focus:outline-none focus:border-primary shadow-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0 [appearance:textfield]"
                  />
                </div>
              </div>

              {/* Upload File */}
              <div>
                <label
                  htmlFor={`${feedbackFieldId}-file`}
                  className="block text-[13px] font-medium text-text-secondary mb-1.5"
                >
                  Upload File
                </label>
                <div className="relative group w-full bg-white border border-dashed border-[#EBEBEB] hover:border-primary/60 rounded-xl transition-all h-[90px] flex flex-col items-center justify-center cursor-pointer overflow-hidden shadow-sm">
                  <input
                    type="file"
                    id={`${feedbackFieldId}-file`}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        attachment: e.target.files[0],
                      })
                    }
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />

                  {formData.attachment ? (
                    <div className="flex flex-col items-center gap-1 z-0 px-4 text-center">
                      <svg
                        className="w-6 h-6 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-[13px] font-medium text-text-primary truncate max-w-full">
                        {formData.attachment.name}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1 z-0 pointer-events-none">
                      <img
                        src={uploadIcon}
                        alt="Upload"
                        className="w-6 h-6 mb-0.5"
                      />
                      <div className="text-[13px]">
                        <span className="text-primary font-medium">
                          Click To Upload
                        </span>
                        <span className="text-text-muted">
                          {' '}
                          or drag and drop
                        </span>
                      </div>
                      <span className="text-[11px] text-[#A0AEC0]">
                        Allow : PDF, Image, Media (Max : 50MB)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 pt-4 flex justify-end shrink-0 border-t border-[#EBEBEB]">
            <button
              type="submit"
              disabled={isSubmitting}
              className="h-[44px] px-6 bg-primary text-white text-[14px] font-medium rounded-lg flex items-center justify-center gap-2 hover:opacity-95 transition-all shadow-[0_4px_10px_rgba(80,171,61,0.2)] disabled:opacity-70"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              {!isSubmitting && (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        </form>
      </SideDrawer>
    </DashboardPage>
  );
}
